// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Autosuggest from 'react-autosuggest';

// Actions
import {
  fetchMapLayersByLayer, fetchMapLayersByLayerSearch, clearSearchText,
  fetchKMLBySourceBoundingBoxZoom
} from '../actions';
import { fetchProfile } from '../../authentication/actions';

// Shapes
import {
  mapLayerShape, searchShape, searchSuggestionsShape,
  gMapsViewportPropertiesShape, projectCategoryFromCSSShape
} from '../propTypesShapes';
import { profileDataShape, userLocationShape } from '../../authentication/propTypesShapes';

// Assets
import './SearchControl.css';

// Constants
import { LEFT_PANE } from '../constants';


const getSuggestionValue = suggestion => suggestion.match;
const renderSuggestion = (suggestion, { query, isHighlighted }) => (
  <div className={`Suggestion ${isHighlighted ? 'highlighted' : ''}`}>
    {suggestion.match}
  </div>
);
const shouldRenderSuggestions = value => {
  return value.trim().length > 2;
};

class SearchControl extends React.PureComponent {
  static propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
    token: PropTypes.string,
    profileData: profileDataShape,
    userLocation: userLocationShape,
    search: searchShape,
    mapViewportProperties: gMapsViewportPropertiesShape,
    selectedMapLayer: mapLayerShape,
    searchSuggestions: searchSuggestionsShape,
    projectCategoryFromCSS: projectCategoryFromCSSShape
  };

  constructor(props) {
    super(props);
    const { search } = props;

    this.state = {
      value: search ? search.filter: '',
      suggestions: []
    };

    this.handleOnSearchInputChange = this.handleOnSearchInputChange.bind(this);
    this.handleOnSearchInputKeyPress = this.handleOnSearchInputKeyPress.bind(this);
    this.handleOnSuggestionsFetchRequested = this.handleOnSuggestionsFetchRequested.bind(this);
    this.handleOnSuggestionsClearRequested = this.handleOnSuggestionsClearRequested.bind(this);
    this.handleOnValueClearRequested = this.handleOnValueClearRequested.bind(this);
    this.handleOnSuggestionSelected = this.handleOnSuggestionSelected.bind(this);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const { token, userLocation, search, searchSuggestions, mapViewportProperties, dispatch } = newProps;

    if (search !== this.props.search && search && search.mapLayer) {
      dispatch(fetchKMLBySourceBoundingBoxZoom(token, search.mapLayer, mapViewportProperties, userLocation));
      this.showOrganizationsList();
    }

    if (searchSuggestions !== this.props.searchSuggestions && searchSuggestions) {
      this.setState({ suggestions: searchSuggestions.matches });
    }
  }

  showOrganizationsList() {
    const { history, location } = this.props;

    if ( !(location.hash === LEFT_PANE.HASH_LINK.LISTING && location.search === '') ) {
      history.push(`/${LEFT_PANE.HASH_LINK.LISTING}`);
    }
  }

  showLayersMenu() {
    const { history, location } = this.props;

    if ( !(location.hash === LEFT_PANE.HASH_LINK.HOME) ) {
      history.push(`/${LEFT_PANE.HASH_LINK.HOME}`);
    }
  }

  handleOnSearchInputChange(event, { newValue, method }) {
    this.setState({ value: newValue });
  }

  fetchMapLayers = (filter) => {
    const {
      token, profileData, selectedMapLayer, projectCategoryFromCSS, dispatch
    } = this.props;
    const mapLayerId = selectedMapLayer ? selectedMapLayer.id : '';
    const query = {
      filter,
      ids: projectCategoryFromCSS?.layerIds
    };

    dispatch(fetchMapLayersByLayer(token, profileData.userName, mapLayerId, query));
  };

  handleOnSearchInputKeyPress(event) {
    if (event.key === 'Enter') {
      this.fetchMapLayers(this.state.value);
    }
  }

  handleOnSuggestionSelected(event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) {
    if (method !== 'enter') {
      this.fetchMapLayers(suggestionValue);
    }
  }

  handleOnSuggestionsFetchRequested({ value, reason }) {
    const { token, selectedMapLayer, projectCategoryFromCSS, dispatch } = this.props;
    const mapLayerId = selectedMapLayer ? selectedMapLayer.id : '';
    const query = {
      text: value,
      ids: projectCategoryFromCSS?.layerIds
    };

    dispatch(fetchMapLayersByLayerSearch(token, mapLayerId, query));
  }

  handleOnSuggestionsClearRequested() {
    this.setState({ suggestions: [] });
  }

  handleOnValueClearRequested() {
    this.setState({ value: '' }, () => {
      const { token, selectedMapLayer, mapViewportProperties, dispatch } = this.props;

      if (selectedMapLayer) {
        dispatch(clearSearchText());
        dispatch(fetchKMLBySourceBoundingBoxZoom(token, selectedMapLayer, mapViewportProperties));
        this.showOrganizationsList();
      } else {
        dispatch(fetchProfile(token));
        this.showLayersMenu();
      }
    });
  }

  render() {
    const { selectedMapLayer } = this.props;
    const { value, suggestions } = this.state;
    const inputProps = {
      placeholder: selectedMapLayer ? 'Search for any item' : 'Search for an organisation',
      value,
      onChange: this.handleOnSearchInputChange,
      onKeyPress: this.handleOnSearchInputKeyPress
    };

    return (
      <Autosuggest
        theme={{
          container: 'SearchControl',
          input: 'SearchControl_Input',
          suggestionsList: 'SearchControl_SuggestionList',
          suggestion: 'SearchControl_Suggestion'
        }}
        suggestions={suggestions}
        onSuggestionsFetchRequested={this.handleOnSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.handleOnSuggestionsClearRequested}
        onSuggestionSelected={this.handleOnSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        shouldRenderSuggestions={shouldRenderSuggestions}
        inputProps={inputProps}
        renderInputComponent={inputProps => (
          <div className="InputContainer d-flex">
            <input {...inputProps} />
            <div className="ClearSearchText btn p-0"
              onClick={this.handleOnValueClearRequested}
            >
              <span className="icon-search-x" />
            </div>
          </div>
        )}
        // alwaysRenderSuggestions
      />
    );
  }
}

export default connect(({ authentication, home }) => ({
  token: authentication.token,
  userLocation: authentication.userLocation,
  profileData: authentication.profile ? authentication.profile.data : null,
  search: home.search,
  mapViewportProperties: home.mapViewportProperties,
  selectedMapLayer: home.selectedMapLayer,
  searchSuggestions: home.searchSuggestions,
  projectCategoryFromCSS: home.projectCategoryFromCSS
}), null)(SearchControl);
